import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GripVertical, LayoutList, Loader2, RotateCcw, Eye, List } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Visual drag-and-drop reorderer for the property edit modal's sections.
 * Two modes:
 *  - List — compact drag rows (fast to skim, keyboard-friendly ▲▼ buttons)
 *  - Visual — cards populated with real content from the first live property
 *            so the admin sees roughly what the modal will look like
 * Order is persisted through /admin/property-form-layout on every drop.
 */
export default function PropertyFormLayout() {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [order, setOrder] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [mode, setMode] = useState("list"); // "list" | "visual"
  const [sampleProperty, setSampleProperty] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data }, propsResp] = await Promise.all([
          axios.get(`${API}/admin/property-form-layout`, { withCredentials: true }),
          axios.get(`${API}/properties`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setSections(data.sections || []);
        setOrder(data.order || []);
        // Pick the first property with a filled gallery as the "sample" so
        // Visual mode can render realistic preview cards.
        const props = propsResp.data || [];
        const best = props.find((p) => (p.gallery || []).length > 0) || props[0] || null;
        setSampleProperty(best);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load form layout");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const persist = async (nextOrder) => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/property-form-layout`, { order: nextOrder }, { withCredentials: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const move = (id, delta) => {
    const idx = order.indexOf(id);
    if (idx === -1) return;
    const target = idx + delta;
    if (target < 0 || target >= order.length) return;
    const next = order.slice();
    const [x] = next.splice(idx, 1);
    next.splice(target, 0, x);
    setOrder(next);
    persist(next);
  };

  const resetToDefault = () => {
    const defaultOrder = sections.map((s) => s.id);
    setOrder(defaultOrder);
    persist(defaultOrder);
    toast.success("Reset to default order");
  };

  // Native HTML5 drag-and-drop — no external DnD library needed.
  const dragOver = (e, overId) => {
    e.preventDefault();
    if (!dragId || dragId === overId) return;
    const from = order.indexOf(dragId);
    const to = order.indexOf(overId);
    if (from === -1 || to === -1) return;
    const next = order.slice();
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    setOrder(next);
  };

  const dragEnd = () => {
    if (dragId) persist(order);
    setDragId(null);
  };

  const labelById = Object.fromEntries(sections.map((s) => [s.id, s.label]));

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading form layout…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="property-form-layout">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <LayoutList className="h-4 w-4" />
            Property Form Layout
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Reorder the sections shown in the property edit modal. Switch to
            <strong> Visual </strong> mode to see each section rendered with
            real content from an existing property. Changes save automatically.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {saving && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> saving
            </span>
          )}
          {/* Mode toggle — segmented control. */}
          <div className="inline-flex border border-border rounded overflow-hidden" role="tablist">
            <button
              type="button"
              onClick={() => setMode("list")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${
                mode === "list" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
              }`}
              data-testid="pfl-mode-list"
              aria-selected={mode === "list"}
            >
              <List className="h-3 w-3" /> List
            </button>
            <button
              type="button"
              onClick={() => setMode("visual")}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${
                mode === "visual" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
              }`}
              data-testid="pfl-mode-visual"
              aria-selected={mode === "visual"}
            >
              <Eye className="h-3 w-3" /> Visual
            </button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={resetToDefault}
            data-testid="pfl-reset"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset default order
          </Button>
        </div>
      </div>

      {mode === "list" ? (
        <ol className="border border-border rounded divide-y bg-background" data-testid="pfl-list">
          {order.map((id, idx) => (
            <li
              key={id}
              draggable
              onDragStart={() => setDragId(id)}
              onDragOver={(e) => dragOver(e, id)}
              onDragEnd={dragEnd}
              onDrop={(e) => e.preventDefault()}
              className={`flex items-center gap-3 px-3 py-3 cursor-move transition-colors ${
                dragId === id ? "bg-blue-50/70 opacity-70" : "hover:bg-muted/40"
              }`}
              data-testid={`pfl-row-${id}`}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-xs font-mono w-6 text-muted-foreground shrink-0">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <span className="text-sm font-medium flex-1">
                {labelById[id] || id}
              </span>
              <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                {id}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => move(id, -1)}
                  disabled={idx === 0}
                  className="h-7 w-7 p-0"
                  data-testid={`pfl-up-${id}`}
                >
                  ▲
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => move(id, 1)}
                  disabled={idx === order.length - 1}
                  className="h-7 w-7 p-0"
                  data-testid={`pfl-down-${id}`}
                >
                  ▼
                </Button>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <div className="space-y-3" data-testid="pfl-visual">
          <div className="text-xs text-muted-foreground bg-blue-50/60 border border-blue-200 rounded px-3 py-2">
            {sampleProperty
              ? <>Previewing content from <strong>{sampleProperty.title || sampleProperty.property_id}</strong>. Drag any card to reorder — the property edit modal will follow this order.</>
              : <>No sample property found — showing placeholder previews.</>
            }
          </div>
          <div className="grid grid-cols-1 gap-3">
            {order.map((id, idx) => (
              <div
                key={id}
                draggable
                onDragStart={() => setDragId(id)}
                onDragOver={(e) => dragOver(e, id)}
                onDragEnd={dragEnd}
                onDrop={(e) => e.preventDefault()}
                className={`border rounded-md bg-background shadow-sm transition-all cursor-move overflow-hidden ${
                  dragId === id ? "border-blue-400 ring-2 ring-blue-200 opacity-80" : "border-border hover:border-blue-300"
                }`}
                data-testid={`pfl-card-${id}`}
              >
                <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/40">
                  <GripVertical className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm font-medium flex-1 truncate">
                    {labelById[id] || id}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => move(id, -1)} disabled={idx === 0} className="h-6 w-6 p-0" data-testid={`pfl-vup-${id}`}>▲</Button>
                    <Button variant="ghost" size="sm" onClick={() => move(id, 1)} disabled={idx === order.length - 1} className="h-6 w-6 p-0" data-testid={`pfl-vdown-${id}`}>▼</Button>
                  </div>
                </div>
                <div className="p-3">
                  <SectionPreview id={id} property={sampleProperty} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionPreview — renders a compact mock of what a given section will look
// like in the property edit modal, populated with real values from the sample
// property when available. This is READ-ONLY (no side effects, no fetches),
// so it can be dropped into the drag-and-drop cards without expensive rerenders.
// ---------------------------------------------------------------------------
function SectionPreview({ id, property }) {
  const p = property || {};
  const gallery = Array.isArray(p.gallery) ? p.gallery : [];
  const cover = gallery[0]?.url || gallery[0] || null;
  const emptyHint = (
    <p className="text-[11px] text-muted-foreground italic">— no preview data —</p>
  );

  // Keep every branch under ~4 lines so the whole preview stays visually
  // consistent (compact rectangles rather than full-height rows).
  switch (id) {
    case "title":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Title</div>
          <div className="text-sm font-medium truncate">{p.title || "Sample property title"}</div>
        </div>
      );
    case "listing_status":
      return (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-full mb-1">Listing status</span>
          {["For Sale", "Test", "Featured"].map((s) => (
            <span key={s} className="text-[10px] px-2 py-0.5 rounded-full border bg-muted/50">{s}</span>
          ))}
        </div>
      );
    case "page_display":
      return (
        <div className="text-xs text-muted-foreground">Toggle whether this property appears on the public site and how it links.</div>
      );
    case "import_url":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Import URL</div>
          <div className="border rounded px-2 py-1 text-[11px] font-mono truncate bg-muted/30">https://rightmove.co.uk/…</div>
        </div>
      );
    case "price":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Price</div>
          <div className="text-lg font-serif font-semibold">£{p.price ? Number(p.price).toLocaleString() : "450,000"}</div>
        </div>
      );
    case "address":
      return (
        <div className="text-xs leading-5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Address</div>
          <div>{p.cms_House_Name_Number || "12"} {p.street_name || "Sample Street"}</div>
          <div className="text-muted-foreground">{p.postcode || "SW1A 1AA"}</div>
        </div>
      );
    case "general_details":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">General Details</div>
          <div className="text-xs">{p.property_type || "Detached"} · {p.tenure || "Freehold"} · Band {p.council_tax_band || "D"}</div>
          <div className="text-xs text-muted-foreground">{p.garden_facing || "South"} · {p.nearest_stations || "0.5 mi station"}</div>
        </div>
      );
    case "description":
      return (
        <div className="text-xs text-muted-foreground line-clamp-3">
          {p.description || "A charming property in a sought-after location, offering spacious rooms and modern finishes throughout."}
        </div>
      );
    case "video":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Video URL</div>
          <div className="text-xs">{p.video_url ? <span className="font-mono truncate block">{p.video_url}</span> : <span className="text-muted-foreground">No video attached</span>}</div>
        </div>
      );
    case "gallery":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Gallery ({gallery.length})</div>
          {cover ? (
            <img src={cover} alt="" className="h-16 w-full object-cover rounded" />
          ) : (
            <div className="h-16 w-full rounded bg-muted flex items-center justify-center text-[11px] text-muted-foreground">No images</div>
          )}
        </div>
      );
    case "floorplan":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Floorplan</div>
          {p.floorplan_image
            ? <img src={p.floorplan_image} alt="" className="h-16 w-full object-cover rounded" />
            : <div className="h-16 w-full rounded bg-muted flex items-center justify-center text-[11px] text-muted-foreground">No floorplan</div>
          }
        </div>
      );
    case "seo":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">SEO</div>
          <div className="text-xs truncate">{p.seo_title || "Meta title, description & keywords"}</div>
        </div>
      );
    case "agent":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Agent Details</div>
          <div className="flex items-center gap-2">
            {p.agent_logo ? (
              <img src={p.agent_logo} alt="" className="h-8 w-8 object-cover rounded-full border" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-[10px] text-muted-foreground">A</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{p.agent_name || p.agent_contact_name || "No agent assigned"}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {p.agent_email || "agent@example.co.uk"}{p.agent_tel ? ` · ${p.agent_tel}` : ""}
              </div>
            </div>
          </div>
        </div>
      );
    // --- Rightmove groups -------------------------------------------------
    case "rm_type_status":
      return (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-full mb-1">Rightmove · Type & Status</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full border">Detached</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full border">Available</span>
        </div>
      );
    case "rm_address":
      return (
        <div className="text-xs leading-5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">RM · Address & Location</div>
          <div>{p.cms_House_Name_Number || "12"} {p.street_name || "Sample Street"}</div>
          <div className="text-muted-foreground">Lat {p.latitude || "51.5"}, Lng {p.longitude || "-0.12"}</div>
        </div>
      );
    case "rm_dates":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">RM · Dates</div>
          <div className="text-xs">Create · Update · Available</div>
        </div>
      );
    case "rm_price_tenure":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">RM · Price & Tenure</div>
          <div className="text-sm">£{p.price ? Number(p.price).toLocaleString() : "450,000"} · Freehold</div>
        </div>
      );
    case "rm_description":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">RM · Description</div>
          <div className="text-xs text-muted-foreground line-clamp-2">{p.description || "One-paragraph summary sent to Rightmove."}</div>
        </div>
      );
    case "rm_rooms_size":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">RM · Rooms & Size</div>
          <div className="text-xs">{p.beds || 3} bed · {p.baths || 2} bath · 1,240 sqft</div>
        </div>
      );
    case "rm_amenities":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">RM · Amenities</div>
          <div className="flex flex-wrap gap-1.5">
            {["Garden", "Parking", "Balcony"].map((a) => (
              <span key={a} className="text-[10px] px-2 py-0.5 rounded-full border bg-muted/40">{a}</span>
            ))}
          </div>
        </div>
      );
    case "rm_regulatory":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">RM · Regulatory</div>
          <div className="text-xs text-muted-foreground">EPC · Council Tax Band · Tenure Info</div>
        </div>
      );
    case "rm_media":
      return (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">RM · Media</div>
          <div className="text-xs text-muted-foreground">{gallery.length || 6} images · virtual tour · brochure</div>
        </div>
      );
    default:
      return emptyHint;
  }
}
